import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Paper,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Stack,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button
} from '@mui/material';
import { Visibility, Download, Refresh } from '@mui/icons-material';
import axios from 'axios';
import dayjs from 'dayjs';
import { useToast } from '../../../../contexts/ToastContext';
import {
  downloadAuthenticatedPdf,
  viewAuthenticatedPdf
} from '../../../../utils/authenticatedPdf';

const API_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * List published duty rosters with View / Download PDF and optional base filter.
 */
const PublishedRostersPanel = ({
  baseOptions = [],
  selectedBaseCity = '',
  onBaseCityChange,
  refreshKey = 0,
  compact = false
}) => {
  const { showToast } = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedBaseCity) params.set('baseCity', selectedBaseCity);
      const res = await axios.get(`${API_URL}/ops/crew-duty-roster/published?${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setRows(res.data || []);
    } catch (error) {
      console.error(error);
      showToast(error.response?.data?.message || 'Failed to load published rosters', 'error');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [selectedBaseCity, showToast]);

  useEffect(() => {
    fetchList();
  }, [fetchList, refreshKey]);

  const baseFilterOptions = useMemo(() => {
    const fromCrew = (baseOptions || []).filter(Boolean);
    const fromRows = rows.map((r) => r.baseCity).filter(Boolean);
    return [...new Set([...fromCrew, ...fromRows])].sort((a, b) => a.localeCompare(b));
  }, [baseOptions, rows]);

  const openPdf = async (row, download = false) => {
    if (!row?._id) return;
    setBusyId(row._id);
    try {
      const url = `${API_URL}/ops/crew-duty-roster/published/${row._id}/pdf${download ? '?download=1' : ''}`;
      const name = row.pdfFileName || `duty-roster-v${row.version}.pdf`;
      if (download) {
        await downloadAuthenticatedPdf(url, name);
      } else {
        await viewAuthenticatedPdf(url);
      }
    } catch (error) {
      showToast(error.message || 'Failed to open PDF', 'error');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Paper sx={{ p: compact ? 2 : 2.5, mb: 2 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        alignItems={{ sm: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 1.5 }}
      >
        <Box>
          <Typography variant="h6">Published Rosters</Typography>
          <Typography variant="body2" color="text.secondary">
            View or download immutable published PDFs. Filter by crew base city.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Base</InputLabel>
            <Select
              label="Base"
              value={selectedBaseCity}
              onChange={(e) => onBaseCityChange?.(e.target.value)}
            >
              <MenuItem value="">All bases</MenuItem>
              {baseFilterOptions.map((city) => (
                <MenuItem key={city} value={city}>{city}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            size="small"
            startIcon={<Refresh />}
            onClick={fetchList}
            disabled={loading}
          >
            Refresh
          </Button>
        </Stack>
      </Stack>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Version</TableCell>
              <TableCell>Base</TableCell>
              <TableCell>Period</TableCell>
              <TableCell>Published</TableCell>
              <TableCell>By</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <CircularProgress size={22} sx={{ my: 1 }} />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No published rosters{selectedBaseCity ? ` for ${selectedBaseCity}` : ''}.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row._id} hover>
                  <TableCell>
                    <Chip size="small" label={`v${row.version}`} color="primary" variant="outlined" />
                  </TableCell>
                  <TableCell>{row.baseCity || 'All bases'}</TableCell>
                  <TableCell>
                    {dayjs(row.periodStart).format('DD MMM YYYY')}
                    {' – '}
                    {dayjs(row.periodEnd).format('DD MMM YYYY')}
                  </TableCell>
                  <TableCell>{dayjs(row.publishedAt).format('DD MMM YYYY HH:mm')}</TableCell>
                  <TableCell>
                    {row.publishedBy
                      ? `${row.publishedBy.firstName || ''} ${row.publishedBy.lastName || ''}`.trim()
                      : '—'}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="View PDF">
                      <span>
                        <IconButton
                          size="small"
                          disabled={busyId === row._id}
                          onClick={() => openPdf(row, false)}
                          aria-label="View published roster PDF"
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Download PDF">
                      <span>
                        <IconButton
                          size="small"
                          disabled={busyId === row._id}
                          onClick={() => openPdf(row, true)}
                          aria-label="Download published roster PDF"
                        >
                          <Download fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default PublishedRostersPanel;
